import { useMutation, useQuery, useQueryClient } from "react-query";
import { useClientInfo } from "atoms/clientsAtom";

import {
  addClient,
  addClientDate,
  addDiscountItem,
  clientTransaction,
  deleteClientDate,
  deleteClientTransaction,
  deleteDiscountItem,
  editClient,
  getClientAccount,
  getClientDate,
  getClientTransactions,
  getDiscountItems,
  setClientAccount,
  transactionCalculation,
} from "./index";

const useOnSuccessClients = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ["get-clients"] });
  };
};

export const useAddClient = () => {
  const onSuccess = useOnSuccessClients();

  return useMutation(["add-client"], addClient, {
    onSuccess,
  });
};

export const useEditClient = () => {
  const onSuccess = useOnSuccessClients();

  return useMutation(["edit-client"], editClient, {
    onSuccess,
  });
};

export const useAddDiscountItem = () => {
  const queryClient = useQueryClient();

  return useMutation(["add-discount-item"], addDiscountItem, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-discount-items"] });
    },
  });
};

export const useGetDiscountItems = (clientid: string) => {
  return useQuery(["get-discount-items"], () => getDiscountItems(clientid), {
    enabled: !!clientid,
  });
};

export const useClientTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation(["client-transaction"], clientTransaction, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-client-transaction"] });
      queryClient.invalidateQueries({
        queryKey: ["get-transaction-calculation"],
      });
    },
  });
};

export const useGetClientTransaction = (clientid: string) => {
  return useQuery(
    ["get-client-transaction"],
    () => getClientTransactions(clientid),
    { enabled: !!clientid }
  );
};

export const useGetClientDate = (clientid: string) => {
  return useQuery(
    ["get-client-date", clientid],
    () => getClientDate(clientid),
    {
      enabled: !!clientid,
    }
  );
};

export const useGetClientAccount = (
  clientid: string,
  onSuccess?: ((data: any) => void) | undefined,
  enabled?: boolean
) => {
  return useQuery(
    ["get-client-account", clientid],
    () => getClientAccount(clientid),
    { onSuccess, enabled }
  );
};

export const useDeleteDiscountItem = () => {
  const queryClient = useQueryClient();

  return useMutation(["delete-discount-item"], deleteDiscountItem, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-discount-items"] });
    },
  });
};

export const useDeleteUserTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation(["delete-client-transaction"], deleteClientTransaction, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-client-transaction"] });
      queryClient.invalidateQueries({
        queryKey: ["get-transaction-calculation"],
      });
    },
  });
};

export const useDeleteClientDate = () => {
  const queryClient = useQueryClient();
  const { setClientInfoState } = useClientInfo();

  return useMutation(["delete-client-date"], deleteClientDate, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-client-date"] });
      setClientInfoState((prev) => ({
        ...prev,
        selectedMarketingId: "",
      }));
    },
  });
};

export const useAddClientDate = () => {
  const queryClient = useQueryClient();
  const { setClientInfoState } = useClientInfo();

  return useMutation(["add-client-date"], addClientDate, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["get-client-date"] });
      setClientInfoState((prev) => ({
        ...prev,
        showAddMarketingFields: false,
      }));
    },
  });
};

export const useSetClientAccount = () => {
  return useMutation(["set-client-account"], setClientAccount, {
    onSuccess: () => {},
  });
};

export const useGetTransactionCalculation = (clientid: string) => {
  return useQuery(
    ["get-transaction-calculation", clientid],
    () => transactionCalculation(clientid),
    { enabled: !!clientid }
  );
};
